import SiteActions from "../actions/SiteActions";
import { COMMANDS } from "./commands";

/**
 * Pure routing table: command type -> invocation of the existing action.
 * Most entries call the action with the payload object unchanged. A few
 * `SiteActions` methods take positional arguments rather than a single
 * payload object (verified against each action's `on<Action>` handler in
 * `src/stores/SiteDesignStore.js`); for those, the command payload is an
 * array and is spread with `...p` so the real call shape is preserved
 * exactly. No-arg actions ignore the payload.
 * @type {Record<string, (payload: any) => void>}
 */
export const commandMap = {
  // -- design / page --
  [COMMANDS.SET_DESIGN]: (p) => SiteActions.setDesign(p),
  [COMMANDS.UPDATE_DESIGN]: (p) => SiteActions.updateDesign(p),
  [COMMANDS.SAVE_JSON]: (p) => SiteActions.saveJSON(p),
  [COMMANDS.ADD_PAGE]: (p) => SiteActions.addPage(p),
  [COMMANDS.REMOVE_PAGE]: (p) => SiteActions.removePage(p),
  [COMMANDS.DUPLICATE_PAGE]: (p) => SiteActions.duplicatePage(p),
  [COMMANDS.UPDATE_PAGE]: (p) => SiteActions.updatePage(p),
  [COMMANDS.UPDATE_PAGE_NAME]: (p) => SiteActions.updatePageName(p),
  [COMMANDS.UPDATE_PAGE_WP_TEMPLATE]: (p) => SiteActions.updatePageWpTemplate(p),
  [COMMANDS.TOGGLE_PAGE_IS_PUBLISHABLE]: (p) => SiteActions.togglePageIsPublishable(p),
  [COMMANDS.SET_AS_HOME_PAGE]: (p) => SiteActions.setAsHomePage(p),
  [COMMANDS.ADD_FOLDER]: (p) => SiteActions.addFolder(p),
  [COMMANDS.REMOVE_FOLDER]: (p) => SiteActions.removeFolder(p),
  [COMMANDS.UPDATE_THEME]: (p) => SiteActions.updateTheme(p),

  // -- global code / code page exceptions --
  [COMMANDS.ADD_GLOBAL_CODE]: (p) => SiteActions.addGlobalCode(p),
  [COMMANDS.UPDATE_GLOBAL_CODE]: (p) => SiteActions.updateGlobalCode(p),
  [COMMANDS.REMOVE_GLOBAL_CODE]: (p) => SiteActions.removeGlobalCode(p),
  // positional: [pageId, includeInGlobalCode]
  [COMMANDS.ADD_CODE_PAGE_EXCEPTION]: (p) => SiteActions.addCodePageException(...p),

  // -- canvas sets --
  [COMMANDS.CREATE_CANVAS_SET]: () => SiteActions.createCanvasSet(),
  [COMMANDS.REMOVE_CANVAS_SET]: (p) => SiteActions.removeCanvasSet(p),
  [COMMANDS.DUPLICATE_CANVAS_SET]: (p) => SiteActions.duplicateCanvasSet(p),
  [COMMANDS.MAKE_CANVAS_SET_DEFAULT]: (p) => SiteActions.makeCanvasSetDefault(p),
  [COMMANDS.ADD_CANVAS_TO_CANVAS_SET]: (p) => SiteActions.addCanvasToCanvasSet(p),
  [COMMANDS.REMOVE_CANVAS_FROM_CANVAS_SET]: (p) => SiteActions.removeCanvasFromCanvasSet(p),
  [COMMANDS.UPDATE_CANVAS_SET]: (p) => SiteActions.updateCanvasSet(p),

  // -- blocks --
  [COMMANDS.ADD_BLOCK]: (p) => SiteActions.addBlock(p),
  [COMMANDS.REMOVE_BLOCK]: (p) => SiteActions.removeBlock(p),
  [COMMANDS.REPLACE_BLOCK]: (p) => SiteActions.replaceBlock(p),
  [COMMANDS.DUPLICATE_BLOCK]: (p) => SiteActions.duplicateBlock(p),
  [COMMANDS.UPDATE_BLOCK]: (p) => SiteActions.updateBlock(p),
  [COMMANDS.UPDATE_BLOCK_NAME]: (p) => SiteActions.updateBlockName(p),
  [COMMANDS.COPY_BLOCK_INTO_BLOCK]: (p) => SiteActions.copyBlockIntoBlock(p),
  [COMMANDS.CONVERT_TO_SITE_BLOCK]: (p) => SiteActions.convertToSiteBlock(p),
  [COMMANDS.CONVERT_TO_PAGE_BLOCK]: (p) => SiteActions.convertToPageBlock(p),
  [COMMANDS.ADD_SITE_BLOCK]: (p) => SiteActions.addSiteBlock(p),
  [COMMANDS.REMOVE_SITE_BLOCK]: (p) => SiteActions.removeSiteBlock(p),
  [COMMANDS.REPLACE_SITE_BLOCK]: (p) => SiteActions.replaceSiteBlock(p),

  // -- block states --
  [COMMANDS.ADD_BLOCK_STATE]: (p) => SiteActions.addBlockState(p),
  [COMMANDS.REMOVE_BLOCK_STATE]: (p) => SiteActions.removeBlockState(p),
  [COMMANDS.DUPLICATE_BLOCK_STATE]: (p) => SiteActions.duplicateBlockState(p),
  [COMMANDS.UPDATE_BLOCK_STATE_NAME]: (p) => SiteActions.updateBlockStateName(p),

  // -- elements --
  [COMMANDS.ADD_ELEMENT]: (p) => SiteActions.addElement(p),
  [COMMANDS.ADD_ELEMENT_FROM_UPLOADING_ASSET]: (p) => SiteActions.addElementFromUploadingAsset(p),
  // positional: [elementId, skipSave]
  [COMMANDS.REMOVE_ELEMENT]: (p) => SiteActions.removeElement(...p),
  // positional: [elementId, options]
  [COMMANDS.DUPLICATE_ELEMENT]: (p) => SiteActions.duplicateElement(...p),
  [COMMANDS.DUPLICATE_ELEMENT_GROUP]: (p) => SiteActions.duplicateElementGroup(p),
  [COMMANDS.REMOVE_ELEMENT_GROUP]: (p) => SiteActions.removeElementGroup(p),
  [COMMANDS.REMOVE_SELECTED_ELEMENTS]: (p) => SiteActions.removeSelectedElements(p),
  [COMMANDS.UPDATE_ELEMENT_NAME]: (p) => SiteActions.updateElementName(p),
  [COMMANDS.UPDATE_ITEM_NAME]: (p) => SiteActions.updateItemName(p),
  [COMMANDS.ADD_DESIGN_ITEM]: (p) => SiteActions.addDesignItem(p),
  // positional: [element, elementId, aspectRatio]
  [COMMANDS.RESIZE_TO_ASPECT_RATIO]: (p) => SiteActions.resizeToAspectRatio(...p),

  // -- ordering / layout --
  // positional: [item, target]
  [COMMANDS.MOVE_ITEM]: (p) => SiteActions.moveItem(...p),
  [COMMANDS.MOVE_LAYER]: (p) => SiteActions.moveLayer(p),
  [COMMANDS.SORT_BLOCK_BY_READING_ORDER]: (p) => SiteActions.sortBlockByReadingOrder(p),
  [COMMANDS.SORT_PAGE_BY_READING_ORDER]: (p) => SiteActions.sortPageByReadingOrder(p),
  [COMMANDS.TOGGLE_ITEMS_LOCK_STATUS]: () => SiteActions.toggleItemsLockStatus(),
  [COMMANDS.TOGGLE_ELEMENT_GROUP_LOCK]: (p) => SiteActions.toggleElementGroupLock(p),

  [COMMANDS.RECORD_RECENT_PEXELS_PHOTO]: (p) => SiteActions.recordRecentPexelsPhoto(p),

  // -- clipboard --
  [COMMANDS.PASTE]: (p) => SiteActions.paste(p),
  [COMMANDS.PASTE_INTO]: (p) => SiteActions.pasteInto(p),
  [COMMANDS.SPECIAL_PASTE]: (p) => SiteActions.specialPaste(p),

  // -- history --
  [COMMANDS.UNDO]: () => SiteActions.undo(),
  [COMMANDS.REDO]: () => SiteActions.redo(),
};
